import React, { Component } from 'react'
import { connect } from "react-redux"
import trans from 'trans';

import { routes } from "app/components/app"
import { Section, Spinner } from "@bdfx/mergado-ui-kit"

import {
    eshopId,
    getAlternativeTemplate,
    getCustomPalettes,
    getDefaultPalettes,
    getGradients,
    getProjectCollageLayouts,
    getProjectSettings,
    getQueries,
    getSizes,
    getTemplate,
    loadTemplateGallery,
    projectId,
} from "app/helpers/api"

import newTemplate from "template-edit/helpers/newTemplate"

import {
    setPalettesToStore,
    setGradientsToStore,
    setSizesToStore,
    setIsAlt,
} from "template-edit/reducers"

import {
    setActiveRoute,
    setGuides,
    setQueriesToStore
} from "actions"

import Editor from "./containers/Editor"
import SectionLoader from "app/components/section-loader";

import {
    CollageLayoutType,
    CustomPalettesFormType,
    emptyEffects,
    Gradient,
    Palette, Query,
    SettingsFormType,
    Template,
    AlternativeTemplate
} from "app/types/model-types";

import { setGuidesFromSizeOrRandomProduct, templateFromGalleryToStore, SOURCE_IMAGE_INDEX } from '../shape-edit/forms/helpers';
import { AxiosResponse } from "axios";
import { RouterType, withRouter } from 'app/components/routes';
import { DEFAULT_ASPECT_RATIO_TYPES } from 'shape-entities/defaults';
import newAlternativeTemplate from './helpers/newAlternativeTemplate';
import { RandomProduct } from 'shape-edit/types';
import { initForm, selectFormValues } from 'app/reducers/form';
import { TemplateGalleryType } from './components/template-gallery';
import { ID, ReduxList } from 'app/types/app-types';
import { debounce, forEach } from "lodash"
import { SizeItem } from 'settings/components/sizes-settings';
import { setActiveShapeId, setActiveGroupId } from '../shape-edit/reducers';
import unique_id from 'app/helpers/unique_id';
import Shape from 'shape-entities/types/shape';


interface Props {
    templates: ReduxList<Template>
    alternativeTemplates: ReduxList<AlternativeTemplate>
    isAlt: boolean
    fromGallery?: boolean
    router: RouterType
    setSizesToStore: typeof setSizesToStore
    setQueriesToStore: typeof setQueriesToStore
    setPalettesToStore: typeof setPalettesToStore
    setGradientsToStore: typeof setGradientsToStore
    setActiveRoute: typeof setActiveRoute
    setActiveShapeId: typeof setActiveShapeId,
    setActiveGroupId: typeof setActiveGroupId,
    setGuides: typeof setGuides
    setIsAlt: typeof setIsAlt
    initForm: typeof initForm
    templateForm: Template
    altTemplateForm: AlternativeTemplate
    randomProduct: RandomProduct
}

interface State {
    isLoadingTemplate: boolean
    isTemplateError: boolean
    isLoadingQueries: boolean
    isLoadingSettings: boolean
    isLoadingCollageLayouts: boolean
}

class ImageEditor extends Component<Props, State> {
    constructor(props: Props) {
        super(props)

        this.state = {
            isLoadingTemplate: null,
            isTemplateError: null,
            isLoadingQueries: null,
            isLoadingSettings: null,
            isLoadingCollageLayouts: null,
        }

        props.setIsAlt(!!props.isAlt)
        props.setActiveShapeId(null)
        props.setActiveGroupId(null)
    }

    normalizeShapeAspectRatios = (shapes: Shape[] | Record<string, Shape>) => {
        Object.values(shapes).forEach((shape) => {
            if (shape.is_aspect_size_ratio === null && DEFAULT_ASPECT_RATIO_TYPES.includes(shape.type)) {
                shape.is_aspect_size_ratio = true
            }
        })
    }

    loadAlternativeTemplate = (alternativeTemplateId: ID, mainTemplateForm: Template = null) => {
        this.setState({ isLoadingTemplate: true })
        if (alternativeTemplateId === 'new') {
            this.initNewTemplateOrAltTemplateForm(true, mainTemplateForm)
        } else {
            getAlternativeTemplate(alternativeTemplateId)
                .then((response: AxiosResponse<AlternativeTemplate>) => {
                    this.normalizeShapeAspectRatios(response.data.shapes)

                    this.props.initForm('altTemplateForm', {...response.data})

                    setGuidesFromSizeOrRandomProduct(response.data.size, this.props.randomProduct)
                })
                .catch(error => {
                    console.error('error', error)
                    this.setState({ isTemplateError: true })
                })
                .finally(() => this.setState({ isLoadingTemplate: false }))
        }
    }

    getQueriesDebounced = debounce(() => {
        const { isAlt, router, randomProduct } = this.props
        const templateId = router.params.template_id || 0
        const alternativeTemplateId = router.params.alt_template_id || 'new'

        this.setState({ isLoadingQueries: true })
        getQueries()
            .then((response: AxiosResponse<Query[]>) => {
                if (templateId && templateId !== 'null') {
                    this.setState({ isLoadingTemplate: true })
                    getTemplate(templateId)
                        .then((response: AxiosResponse<Template>) => {
                            this.normalizeShapeAspectRatios(response.data.shapes)

                            const newTemplateForm = {
                                ...response.data,
                                effects: response.data.effects ? response.data.effects : { ...emptyEffects, project_id: response.data.project_id }
                            }

                            this.props.initForm('templateForm', newTemplateForm)

                            if (!isAlt) {
                                this.props.initForm('altTemplateForm', {})
                                setGuidesFromSizeOrRandomProduct(response.data.size, randomProduct)
                            } else {
                                this.loadAlternativeTemplate(alternativeTemplateId, newTemplateForm)
                            }
                        })
                        .catch(error => {
                            console.error(`error while getting template ${templateId}`, error)
                            this.setState({ isTemplateError: true })

                            //redirect to templates list:
                            this.props.router.navigate(`/eshop/${eshopId}/project/${projectId}/templates`)
                        })
                        .finally(() => this.setState({ isLoadingTemplate: false }))
                } else if (isAlt && alternativeTemplateId) {
                    this.loadAlternativeTemplate(alternativeTemplateId)
                } else if (router.params.action === 'from-gallery') {
                    this.setState({ isLoadingTemplate: false })
                } else if (!this.props.templateForm || !this.props.templateForm.name) {
                    this.initNewTemplateOrAltTemplateForm(false)
                }

                this.props.setQueriesToStore(response.data)
                this.props.setActiveRoute(routes.SETTINGS)
            })
            .finally(() => this.setState({ isLoadingQueries: false }))
    }, 500)

    initNewTemplateOrAltTemplateForm = (isAlt: boolean, mainTemplateForm: Template|null = null) => {  //main template needed only for alt!
        const { randomProduct } = this.props

        this.props.setActiveShapeId(null)
        this.props.setActiveGroupId(null)

        const useStoreTemplate = isAlt ? this.props.altTemplateForm?.project_id : this.props.templateForm?.project_id    //do not rewrite if the template is already in store (set from modal)
        if (useStoreTemplate) {
            //use template already pre-set in store (preset in modal only), redirected to   "edit(-alt)/{template_id}/new"    route
            const newForm = isAlt ? { ...this.props.altTemplateForm } : { ...this.props.templateForm }
            setGuidesFromSizeOrRandomProduct(newForm.size, randomProduct)
        } else {
            const form = !isAlt ? newTemplate() : (mainTemplateForm ? { ...mainTemplateForm } : newAlternativeTemplate())

            if (isAlt) {
            //init new alt template form out from the existing main template
                let newShapes = {}
                if (form?.shapes) {
                    forEach(form.shapes, (shape: Shape) => {
                        const newIndex = shape.index !== SOURCE_IMAGE_INDEX ? unique_id() : shape.index
                        newShapes[newIndex] = {
                            ...shape,
                            id: null,
                            index: newIndex,    //new index??? is this needed?
                            template_id: null,
                            alternative_template_id: null,
                        }
                    })
                }

                this.props.initForm('altTemplateForm', {
                    ...form,
                    id: null,
                    name: null,
                    queries: null,
                    shapes: newShapes,    //copy shapes from the main template?
                    template_id: mainTemplateForm?.id ?? null,
                })
            } else {
                this.props.initForm('templateForm', {
                    ...form,
                })
            }

            if (form.size) {
                setGuidesFromSizeOrRandomProduct(form.size, randomProduct)
            }
        }

        this.setState({ isLoadingTemplate: false })
    }

    componentWillUnmount() {
        this.getQueriesDebounced.cancel()
    }

    componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any): void {
        if (prevProps && prevProps.isAlt !== this.props.isAlt) {
            this.getQueriesDebounced()
            this.props.setActiveShapeId(null)
            this.props.setActiveGroupId(null)
        }
    }

    componentDidMount() {
        const { fromGallery, router } = this.props

        getSizes()
            .then((response: AxiosResponse<SizeItem[]>) => {
                this.props.setSizesToStore(response.data)
            })
            .catch(error => {
                console.error('error while getting queries', error)
            })

        this.getQueriesDebounced()

        this.setState({ isLoadingCollageLayouts: true })
        getProjectCollageLayouts()
            .then((response: AxiosResponse<CollageLayoutType[]>) => {
                this.props.initForm('collageLayoutsForm', response.data)
            })
            .catch(error => {
                console.error('cannot get collage layouts', error)
            })
            .finally(() => this.setState({ isLoadingCollageLayouts: false }))

        getCustomPalettes()
            .then((response: AxiosResponse<CustomPalettesFormType>) => {
                this.props.initForm('customPalettesForm', response.data)
            })
            .catch(error => {
                console.error('cannot get custom palettes', error)
            })

        getDefaultPalettes()
            .then((response: AxiosResponse<Palette[]>) => {
                this.props.setPalettesToStore(response.data)
            })
            .catch(error => {
                console.error('cannot get default palettes', error)
            })

        getGradients()
            .then((response: AxiosResponse<Gradient[]>) => {
                this.props.setGradientsToStore(response.data)
            })
            .catch(error => {
                console.error('cannot get gradients', error)
            })

        this.setState({ isLoadingSettings: true })
        getProjectSettings()
            .then((response: AxiosResponse<SettingsFormType>) => {
                this.props.initForm('settingsForm', response.data)
            })
            .catch(error => {
                console.error('cannot get project settings', error)
            })
            .finally(() => this.setState({ isLoadingSettings: false }))


        //TODO: can alternative template be loaded from the gallery???
        if (fromGallery && router.params.gallery_template_id) {
            this.setState({ isLoadingTemplate: true })
            const template = newTemplate()
            this.props.initForm('templateForm', template)
            loadTemplateGallery(router.params.gallery_template_id)
                .then((response: AxiosResponse<{ templateGallery: TemplateGalleryType, gradients: Gradient[] }>) => {
                    this.props.setGradientsToStore(response.data.gradients)
                    templateFromGalleryToStore(response.data.templateGallery)
                })
                .catch(e => {
                    console.error('cannot load template from gallery', e.response)
                })
                .finally(() => this.setState({ isLoadingTemplate: false }))
        }
    }

    render() {
        const { isLoadingTemplate, isTemplateError, isLoadingCollageLayouts, isLoadingQueries, isLoadingSettings } = this.state
        const { router, isAlt, altTemplateForm } = this.props

        const templateId = !isLoadingTemplate && this.props.router.params.template_id && this.props.router.params.template_id !== 'null' ? this.props.router.params.template_id : 0
        const altTemplateId = !isLoadingTemplate && isAlt ? router.params.alt_template_id : null

        return (
            <div>
                {isLoadingTemplate &&
                    <SectionLoader title={trans('loading_template')} />
                }

                {isLoadingQueries &&
                    <SectionLoader title={<Spinner text={trans('loading_queries')} />} />
                }

                {((isLoadingTemplate === false) && !isLoadingQueries && !isLoadingCollageLayouts && !isLoadingSettings && (!isAlt || Object.keys(altTemplateForm).length > 0)) &&
                    <Editor key={`${templateId}_${altTemplateId}`} templateId={templateId} altTemplateId={altTemplateId} />
                }

                {isTemplateError &&
                    <Section header={trans("template_not_found")} />
                }
            </div>
        )
    }
}

function mapStateToProps(state) {
    return {
        templateForm: selectFormValues(state, 'templateForm'),
        altTemplateForm: selectFormValues(state, 'altTemplateForm'),
        randomProduct: state.randomProduct,
        templates: state.templates,
    }
}

export default connect(mapStateToProps, {
    setQueriesToStore,
    setPalettesToStore,
    setGradientsToStore,
    setSizesToStore,
    setActiveRoute,
    setActiveShapeId,
    setActiveGroupId,
    setGuides,
    setIsAlt,
    initForm,
})(withRouter(ImageEditor));
